import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { ArrowBack } from "@mui/icons-material";
import type { GenericModalProps } from "./types";
import InfoTooltip from "../InfoTooltip/InfoTooltip";
import ModalCloseButton from "./ModalCloseButton";
import {
	MODAL_BODY_DEFAULT_PADDING,
	MODAL_BODY_WITH_FOOTER_PADDING,
	MODAL_FOOTER_PADDING,
	MODAL_FOOTER_PADDING_WHEN_ZERO_BODY,
	MODAL_HEADER_BACK_BUTTON_CLASS,
	MODAL_HEADER_LAYOUT_CLASS,
	MODAL_HEADER_SURFACE_CLASS,
	MODAL_TITLE_CLASS,
	isStandardModalPadding,
} from "@config/theme/modal";

const Modal: React.FC<GenericModalProps> = ({
	isOpen,
	onClose,
	title,
	icon,
	showCloseIcon = true,
	children,
	footer,
	width = "max-w-md",
	height = "auto",
	backgroundColor = "bg-white",
	rounded = "rounded-2xl",
	padding = MODAL_BODY_DEFAULT_PADDING,
	showBackIcon,
	onBack,
	className = "",
	isTooltipEnabled,
	tooltipText,
	titleClassName = MODAL_TITLE_CLASS,
	headerClassName = "",
	overflowVisible = false,
	overlayClassName = "",
	bodyClassName = "",
}) => {
	const getResponsiveWidth = () => {
		if (width === "max-w-md" || width === "max-w-sm" || width === "max-w-lg") {
			return width;
		}
		if (width === "max-w-4xl" || width === "max-w-5xl" || width === "max-w-6xl") {
			return width;
		}
		return width;
	};

	const responsiveWidth = getResponsiveWidth();
	const isZeroPadding = padding === "p-0" || padding === "0";
	const usesSplitFooterPadding = Boolean(footer) && !isZeroPadding && isStandardModalPadding(padding);
	const bodyPadding = isZeroPadding
		? ""
		: usesSplitFooterPadding
			? MODAL_BODY_WITH_FOOTER_PADDING
			: padding;
	const footerPadding = usesSplitFooterPadding
		? MODAL_FOOTER_PADDING
		: isZeroPadding && footer
			? MODAL_FOOTER_PADDING_WHEN_ZERO_BODY
			: isZeroPadding
				? ""
				: bodyPadding;
	const showHeader = Boolean(title || icon || showBackIcon || showCloseIcon);

	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = "hidden";
			document.documentElement.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "unset";
			document.documentElement.style.overflow = "unset";
		}

		return () => {
			document.body.style.overflow = "unset";
			document.documentElement.style.overflow = "unset";
		};
	}, [isOpen]);

	if (!isOpen) return null;

	const isScrollableOverflowTarget = (target: EventTarget | null, boundary: HTMLElement) => {
		let el = target instanceof HTMLElement ? target : null;
		while (el && el !== boundary) {
			if (el.scrollHeight > el.clientHeight + 1) {
				const { overflowY } = window.getComputedStyle(el);
				if (overflowY === "auto" || overflowY === "scroll") {
					return true;
				}
			}
			el = el.parentElement;
		}
		return false;
	};

	const modalContent = (
		<div
			className={`fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-30 p-3 pt-[max(0.75rem,env(safe-area-inset-top))] pb-4 sm:items-center sm:p-4 ${
				overflowVisible ? "overflow-hidden overscroll-none" : "overflow-y-auto"
			} ${overlayClassName}`.trim()}
			role="dialog"
			style={{ top: 0, left: 0, right: 0, bottom: 0 }}
			onScroll={
				overflowVisible
					? event => {
							event.currentTarget.scrollTop = 0;
							event.currentTarget.scrollLeft = 0;
						}
					: undefined
			}
			onWheel={
				overflowVisible
					? event => {
							if (!isScrollableOverflowTarget(event.target, event.currentTarget)) {
								event.preventDefault();
							}
						}
					: undefined
			}>
			<div
				className={`my-auto flex w-full min-h-0 flex-col ${overflowVisible ? "overflow-visible" : "overflow-hidden"} ${responsiveWidth} ${height} ${backgroundColor} ${rounded} relative shadow-xl ${className}`}>
				{showHeader && (
					<div
						className={[
							"relative z-20 shrink-0",
							headerClassName,
							MODAL_HEADER_LAYOUT_CLASS,
							MODAL_HEADER_SURFACE_CLASS,
						]
							.filter(Boolean)
							.join(" ")}>
						<div className="flex min-w-0 flex-1 items-center gap-2 [&_.MuiSvgIcon-root]:text-primary-foreground">
							{showBackIcon && (
								<button type="button" onClick={onBack} className={MODAL_HEADER_BACK_BUTTON_CLASS}>
									<ArrowBack className="h-5 w-5" />
								</button>
							)}
							{icon && <div className="shrink-0 text-primary-foreground">{icon}</div>}
							{title &&
								(typeof title === "string" ? (
									<h2 className={`min-w-0 ${titleClassName}`}>{title}</h2>
								) : (
									<div className={`min-w-0 ${titleClassName}`}>{title}</div>
								))}
							{isTooltipEnabled && <InfoTooltip tooltip={tooltipText} />}
						</div>
						{showCloseIcon && <ModalCloseButton onClose={onClose} />}
					</div>
				)}

				<div
					className={`relative min-h-0 flex-1 ${
						overflowVisible
							? "z-30 overflow-visible"
							: "z-0 overflow-y-auto overflow-x-hidden overscroll-contain"
					} ${bodyPadding} ${bodyClassName}`.trim()}>
					{children}
				</div>

				{footer && (
					<div className={`relative z-20 flex-shrink-0 bg-inherit ${footerPadding}`.trim()}>
						{footer}
					</div>
				)}
			</div>
		</div>
	);

	return createPortal(modalContent, document.body);
};

export default Modal;
